/**
 * Which clock is this timestamp on? — G40-456
 *
 * THE BUG. Every date in this panel is rendered with `moment(value).format()`,
 * which renders in the BROWSER's own timezone and prints no zone label. The
 * API sends a correct UTC instant; the browser quietly re-clocks it to
 * whoever is looking. Support sits in Eastern, so a job worked in San
 * Francisco reads three hours late and nothing on the page says so.
 *
 * On order 64672 the Gopher completed at 20:19:04 UTC — 1:19pm on the clock at
 * the site. The panel showed 4:19pm, the written reply to the requester quoted
 * the Eastern figures, and an on-time Gopher was described as arriving in the
 * late afternoon. A correction had to be sent on 2026-09-08. The order detail
 * view is precisely where someone judges whether a Gopher turned up on time,
 * so it is the view where the distortion costs the most.
 *
 * THE FIX. Render an order's timestamps on the ORDER's clock — the one the two
 * people on the job actually lived — and always name the zone. The zone comes
 * from the API as `order_timezone`, derived there from the order's address.
 *
 * NO NEW DEPENDENCY. `Intl.DateTimeFormat` does timezone-correct rendering
 * natively in every browser this panel supports; moment-timezone is not
 * installed and is not needed. moment is left alone everywhere else.
 */

/**
 * The operator's own clock — the zone a view sits on when it has no order
 * behind it, and the honest last resort when the API sent no zone at all.
 *
 * ⚠️ MUST BE KEPT IN STEP WITH THE BACKEND'S `ADMIN_TIMEZONE`, which is what
 * the CSV exports convert to. Two different "operator zones" would put the
 * screen and the export on different clocks while both claimed to be the
 * operator's — a worse bug than the one this file exists to fix, because the
 * two would disagree in a way nobody is looking for.
 *
 * It is an env var so that changing ADMIN_TIMEZONE does not require a code
 * change here, and a literal default so an unset var cannot blank every date
 * in the panel. Order-scoped views do not use this at all — they render on the
 * order's own clock, supplied per-record by the API.
 */
const OPERATOR_FALLBACK =
    process.env.REACT_APP_OPERATOR_TIMEZONE || 'America/New_York'

/** The operator clock, for views that have no order behind them. */
export function operatorZone() {
    return OPERATOR_FALLBACK
}

/**
 * The caption a non-order view carries, naming the clock it is on.
 *
 * Criterion 4 of G40-456: a view may reasonably stay on a single operator
 * timezone, but it must then say which one. These are lists of OTP logs,
 * coupons, addresses — rows that belong to no single order, so there is no
 * order clock to use.
 */
export function operatorZoneNotice(at = new Date()) {
    let label = OPERATOR_FALLBACK
    try {
        const abbr = new Intl.DateTimeFormat('en-US', {
            timeZone: OPERATOR_FALLBACK,
            timeZoneName: 'short',
        })
            .formatToParts(at)
            .find(p => p.type === 'timeZoneName')
        if (abbr) label = `${OPERATOR_FALLBACK} (${abbr.value})`
    } catch (e) {
        /* an unknown zone must never blank a page over a caption */
    }
    return `Times on this page are shown in ${label}.`
}

/**
 * Render a UTC instant on a named zone's clock, with the zone named.
 *
 * The label is the whole point. "1:19 PM" tells an Eastern reader nothing;
 * "1:19 PM PDT" tells them it is not their own clock.
 *
 * Returns 'N/A' rather than "Invalid date" for anything unusable — the panel
 * is full of nullable timestamps (delivery_eta, request_schedule_time) and
 * moment's default for those was the string "Invalid date".
 */
export function formatInZone(value, timezone, options = {}) {
    const {withParens = false, dateOnly = false, withSeconds = false} = options

    if (value === null || value === undefined || value === '') return 'N/A'

    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return 'N/A'

    const zone = timezone || OPERATOR_FALLBACK

    let parts
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            ...(withSeconds ? {second: '2-digit'} : {}),
            hour12: true,
            timeZoneName: 'short',
        }).formatToParts(date)
    } catch (e) {
        /* An unknown zone string must never blank an order page over a
           displayed time. Fall back to the operator clock, still labelled. */
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone: OPERATOR_FALLBACK,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            ...(withSeconds ? {second: '2-digit'} : {}),
            hour12: true,
            timeZoneName: 'short',
        }).formatToParts(date)
    }

    const get = type => (parts.find(p => p.type === type) || {}).value
    const day = `${get('year')}-${get('month')}-${get('day')}`

    if (dateOnly) return day

    const seconds = withSeconds ? `:${get('second')}` : ''
    const time = `${get('hour')}:${get('minute')}${seconds} ${get(
        'dayPeriod',
    )} ${get('timeZoneName')}`

    return withParens ? `${day} (${time})` : `${day} ${time}`
}

/**
 * One line telling the reader whose clock the page is on, and how sure we are.
 *
 * Criterion 4 of the ticket: a view may sit on a single zone, but it must then
 * SAY which one. `source` comes from the API and is never dressed up — a
 * state-derived zone is presented as inferred, because eleven states span two
 * zones and the state table can only give the predominant one.
 */
export function zoneNotice(timezone, source, at = new Date()) {
    const zone = timezone || OPERATOR_FALLBACK

    let label = zone
    try {
        const abbr = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            timeZoneName: 'short',
        })
            .formatToParts(at)
            .find(p => p.type === 'timeZoneName')
        if (abbr) label = `${zone} (${abbr.value})`
    } catch (e) {
        label = OPERATOR_FALLBACK
    }

    switch (source) {
        case 'coordinates':
            return `Times below are on the order's own clock — ${label}, from the job address.`
        case 'state':
            return `Times below are on ${label}, inferred from the order's state. Some states span two zones, so treat this as approximate.`
        case 'fallback':
        default:
            return `This order has no usable address, so times below are on the operator clock — ${label}, NOT the order's own.`
    }
}
